import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { loadActiveSedes, type Sede } from '../services/sedes';

interface Props {
  // Nombres de sede seleccionados. Vacío = "Todas" (no filtra nada).
  selected: string[];
  onChange: (next: string[]) => void;
}

// Filtro multi-selección de sedes por chips (S-4 multi-sede). "Todas" + una chip
// por sede activa; se puede marcar una, varias o todas. Reutilizable en la
// agenda admin y en la del monitor. Se OCULTA si hay <2 sedes activas: con una
// sola sede el filtro es irrelevante y solo estorbaría. El filtrado es 100% en
// cliente — este componente solo expone los NOMBRES seleccionados; la sede de
// cada sesión ya viene cargada (S-2), aquí no se toca ninguna query del motor.
export default function SedeFilter({ selected, onChange }: Props) {
  const [sedes, setSedes] = useState<Sede[]>([]);

  useEffect(() => {
    // El error se liga y se DESCARTA a propósito, que no es lo mismo que
    // tirarlo: este componente solo ofrece un filtro sobre datos que ya están
    // en pantalla. Si las sedes no se leen, el filtro no aparece —como cuando
    // el club tiene una sola— y no se afirma nada falso sobre nadie. Lo que no
    // podía quedarse así es el `<select>` con el que se ASIGNA sede, que sí
    // decide un dato: ese avisa (GruposPage, SesionesPage, SedesPage).
    void loadActiveSedes().then(({ sedes: filas }) => setSedes(filas));
  }, []);

  if (sedes.length < 2) return null;

  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="sede-filter">
      <MapPin size={14} className="text-text-secondary flex-shrink-0" />
      <button
        type="button"
        onClick={() => onChange([])}
        className={chipClass(selected.length === 0)}
      >
        Todas
      </button>
      {sedes.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => toggle(s.name)}
          className={chipClass(selected.includes(s.name))}
          data-testid={`sede-chip-${s.id}`}
        >
          {s.name}
        </button>
      ))}
    </div>
  );
}

function chipClass(active: boolean): string {
  return `px-3 py-1.5 rounded-full text-sm border transition-colors ${
    active
      ? 'bg-gold-primary text-bg-primary border-gold-primary font-semibold'
      : 'bg-bg-surface text-text-secondary border-bg-border hover:border-gold-primary/40 hover:text-white'
  }`;
}
